/**
 * Push notifications on this device: ask permission, subscribe through the service worker and tell
 * the server (server/lib/push.ts). iPhones only get them in the installed app (iOS 16.4+).
 */
import { jsonInit, request } from "./api";

export type PushState = "unsupported" | "ios-install" | "off" | "on" | "denied";

const supported = () => typeof window !== "undefined" && "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
const isIos = () => /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
const installed = () => window.matchMedia?.("(display-mode: standalone)").matches || (navigator as Navigator & { standalone?: boolean }).standalone === true;

/** The server's VAPID key, or null while push is off there. */
export async function pushKey(): Promise<string | null> {
  return request<{ key: string }>("/api/push/key").then(
    (r) => r.key,
    () => null,
  );
}

async function registration(): Promise<ServiceWorkerRegistration | null> {
  if (!supported()) return null;
  return (await navigator.serviceWorker.getRegistration()) ?? null;
}

export async function pushState(): Promise<PushState> {
  if (!supported()) return isIos() && !installed() ? "ios-install" : "unsupported";
  if (Notification.permission === "denied") return "denied";
  const reg = await registration();
  const sub = await reg?.pushManager.getSubscription();
  return sub && Notification.permission === "granted" ? "on" : "off";
}

function keyBytes(base64url: string): Uint8Array<ArrayBuffer> {
  const b64 = (base64url + "=".repeat((4 - (base64url.length % 4)) % 4)).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(b64);
  const out = new Uint8Array(new ArrayBuffer(raw.length));
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

/** Asks permission (must follow a tap) and subscribes this device. */
export async function enablePush(key: string): Promise<PushState> {
  const permission = await Notification.requestPermission();
  if (permission !== "granted") return permission === "denied" ? "denied" : "off";
  const reg = (await registration()) ?? (await navigator.serviceWorker.ready);
  const sub = (await reg.pushManager.getSubscription()) ?? (await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: keyBytes(key) }));
  await request("/api/me/push", jsonInit("POST", { subscription: sub.toJSON() }));
  return "on";
}

/**
 * If this browser already gets notifications, they now belong to whoever is signed in: a previous
 * account whose session just expired (no sign-out) stops receiving them here. Errors are ignored.
 */
export async function claimPush(): Promise<void> {
  if (!supported() || Notification.permission !== "granted") return;
  const sub = await (await registration())?.pushManager.getSubscription();
  if (sub) await request("/api/me/push", jsonInit("POST", { subscription: sub.toJSON() })).catch(() => undefined);
}

/** Stops notifications on this device (the server forgets it too). Used on sign-out. */
export async function disablePush(): Promise<void> {
  const reg = await registration();
  const sub = await reg?.pushManager.getSubscription();
  if (!sub) return;
  await request("/api/me/push", jsonInit("DELETE", { endpoint: sub.endpoint })).catch(() => undefined);
  await sub.unsubscribe().catch(() => undefined);
}
